<template>
  <div class="theme-editor">
    <!-- あらすじ -->
    <div class="theme-section">
      <h2 class="section-title">{{ t("themePage.synopsisTitle") }}</h2>
      <p class="section-label section-label--with-link">{{ t("themePage.synopsisLabel") }}</p>
      <router-link
        class="synopsis-edit-link"
        :to="{ name: 'detail', params: { novelId: props.novelId } }"
      >{{ t("themePage.synopsisEditLink") }}</router-link>
      <theme-text-field
        v-model="theme['synopsis']"
        name="synopsis"
        :maxlength="3000"
        @validation-change="onFieldValidationChange('synopsis', $event)"
      >
        <template #example>
          <button class="view-example-btn" type="button" @click="openExample('synopsisExampleContent')">
            <IconInfo />{{ t("themePage.viewExample") }}
          </button>
        </template>
      </theme-text-field>
    </div>

    <!-- 登場人物 -->
    <div class="theme-section">
      <h2 class="section-title">{{ t("themePage.charactersTitle") }}</h2>
      <p class="section-label">{{ t("themePage.charactersLabel") }}</p>
      <theme-text-field
        v-model="theme['characters']"
        name="characters"
        :maxlength="3000"
        @validation-change="onFieldValidationChange('characters', $event)"
      >
        <template #example>
          <button class="view-example-btn" type="button" @click="openExample('charactersExampleContent')">
            <IconInfo />{{ t("themePage.viewExample") }}
          </button>
        </template>
      </theme-text-field>
    </div>

    <!-- 終着点 -->
    <div class="theme-section">
      <h2 class="section-title">{{ t("themePage.goalTitle") }}</h2>
      <p class="section-label">{{ t("themePage.goalLabel") }}</p>
      <theme-text-field
        v-model="theme['goal']"
        name="goal"
        :rows="3"
        :maxlength="3000"
        @validation-change="onFieldValidationChange('goal', $event)"
      >
        <template #example>
          <button class="view-example-btn" type="button" @click="openExample('goalExampleContent')">
            <IconInfo />{{ t("themePage.viewExample") }}
          </button>
        </template>
      </theme-text-field>
    </div>

    <!-- テーマ -->
    <div class="theme-section">
      <h2 class="section-title">{{ t("themePage.themeTitle") }}</h2>
      <p class="section-label">{{ t("themePage.themeLabel") }}</p>
      <theme-text-field
        v-model="theme['theme']"
        name="theme"
        :rows="isMobileScreen ? 2 : 1"
        :maxlength="100"
        @validation-change="onFieldValidationChange('theme', $event)"
      >
        <template #example>
          <button class="view-example-btn" type="button" @click="openExample('themeExampleContent')">
            <IconInfo />{{ t("themePage.viewExample") }}
          </button>
        </template>
      </theme-text-field>
    </div>

    <!-- 物語の変化 -->
    <div class="theme-section">
      <h2 class="section-title">{{ t("themePage.storyChangesTitle") }}</h2>
      <p class="section-label">{{ t("themePage.storyChangesLabel") }}</p>
      <theme-text-field
        v-model="theme['story-changes']"
        name="story-changes"
        :maxlength="3000"
        @validation-change="onFieldValidationChange('story-changes', $event)"
      >
        <template #example>
          <button class="view-example-btn" type="button" @click="openExample('storyChangesExampleContent')">
            <IconInfo />{{ t("themePage.viewExample") }}
          </button>
        </template>
      </theme-text-field>
    </div>

    <!-- ログライン -->
    <div class="theme-section">
      <h2 class="section-title">{{ t("themePage.loglineTitle") }}</h2>
      <p class="section-label">{{ t("themePage.loglineLabel") }}</p>
      <theme-text-field
        v-model="theme['logline']"
        name="logline"
        :rows="isMobileScreen ? 2 : 1"
        :maxlength="100"
        @validation-change="onFieldValidationChange('logline', $event)"
      >
        <template #example>
          <button class="view-example-btn" type="button" @click="openExample('loglineExampleContent')">
            <IconInfo />{{ t("themePage.viewExample") }}
          </button>
        </template>
      </theme-text-field>
    </div>

    <!-- ターゲット層 -->
    <div class="theme-section">
      <h2 class="section-title">{{ t("themePage.targetTitle") }}</h2>
      <p class="section-label">{{ t("themePage.targetLabel") }}</p>
      <div class="sub-fields">
        <theme-text-field
          v-model="theme['target-segment']"
          name="target-segment"
          :label="t('themePage.targetSegmentLabel')"
          :rows="isMobileScreen ? 2 : 1"
          :maxlength="100"
          @validation-change="onFieldValidationChange('target-segment', $event)"
        >
          <template #example>
            <button class="view-example-btn" type="button" @click="openExample('targetSegmentExampleContent')">
              <IconInfo />{{ t("themePage.viewExample") }}
            </button>
          </template>
        </theme-text-field>
        <theme-text-field
          v-model="theme['target-preference']"
          name="target-preference"
          :label="t('themePage.targetPreferenceLabel')"
          :rows="isMobileScreen ? 2 : 1"
          :maxlength="100"
          @validation-change="onFieldValidationChange('target-preference', $event)"
        >
          <template #example>
            <button class="view-example-btn" type="button" @click="openExample('targetPreferenceExampleContent')">
              <IconInfo />{{ t("themePage.viewExample") }}
            </button>
          </template>
        </theme-text-field>
        <theme-text-field
          v-model="theme['target-prefer-novels']"
          name="target-prefer-novels"
          :label="t('themePage.targetNovelsLabel')"
          :rows="isMobileScreen ? 2 : 1"
          :maxlength="100"
          @validation-change="onFieldValidationChange('target-prefer-novels', $event)"
        >
          <template #example>
            <button class="view-example-btn" type="button" @click="openExample('targetNovelsExampleContent')">
              <IconInfo />{{ t("themePage.viewExample") }}
            </button>
          </template>
        </theme-text-field>
      </div>
    </div>

    <!-- 執筆予定文字数 -->
    <div class="theme-section">
      <h2 class="section-title">{{ t("themePage.wordLengthTitle") }}</h2>
      <p class="section-label">{{ t("themePage.wordLengthLabel") }}</p>
      <theme-text-field>
        <div class="input-wrapper">
          <input
            type="text"
            id="word-length"
            name="word-length"
            class="word-length"
            v-model="theme['word-length']"
            maxlength="18"
            @blur="formatWordLength"
          />
          <span>{{ t("themePage.wordLengthSuffix") }}</span>
        </div>
      </theme-text-field>
      <div class="annotation">
        <p class="annotation-title">{{ t("themePage.wordLengthReference") }}</p>
        <div class="annotation-grid">
          <span>{{ t("themePage.wordLengthFullNovel") }}</span
          ><span>{{ t("themePage.wordLengthFullNovelValue") }}</span>
          <span>{{ t("themePage.wordLengthMediumNovel") }}</span
          ><span>{{ t("themePage.wordLengthMediumNovelValue") }}</span>
          <span>{{ t("themePage.wordLengthShortNovel") }}</span
          ><span>{{ t("themePage.wordLengthShortNovelValue") }}</span>
          <span>{{ t("themePage.wordLengthFlashFiction") }}</span
          ><span>{{ t("themePage.wordLengthFlashFictionValue") }}</span>
        </div>
      </div>
    </div>

    <div class="save-button-wrapper">
      <button class="save-button" :disabled="!canSave" @click="submit('button')">
        {{ t("themePage.saveButton") }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import Vue, { ref, onMounted, computed, defineProps, onBeforeUnmount, getCurrentInstance } from "vue";
import { cloneDeep, debounce } from "lodash";
import deepEqual from "deep-equal";
import { useI18n } from "vue-i18n-bridge";
import ThemeTextField from "@/components/molecules/ThemeTextField.vue";
import { showNotifyDialog, showExampleDialog, showOkCancelDialog } from "@/lib/dialog";
import type { Detail } from "@/lib/models/theme";
import { usePageEditStore } from "@/stores/pageEdit";
import { useThemeStore } from "@/stores/theme";
import { handleShortcutKeys, TriggerKey } from "@/lib/utils/keyboardShortcuts";
import { ThemeAnalytics, ThemeGenerateAnalytics, ThemeSaveTrigger } from "@/lib/utils/analytics/theme";
import { isMobile } from "@/constants/breakpoints";
import { THEME_FIELD_NAMES, THEME_GENERATE_APPLY_EVENT } from "@/constants/themeGenerate";
import {
  consumePendingThemeGenerateApply,
  type ThemeGenerateFields,
} from "@/lib/utils/themeGenerateApply";
import { scrollToThemeField } from "@/lib/utils/themeMatchingIssues";
import IconInfo from "~icons/figma/Info";

const props = defineProps<{
  novelId: string;
}>();

const { t } = useI18n();

const theme = ref<Record<string, string>>({});
const preTheme = ref<Record<string, string>>({});
const unbindShortcutKeys = ref<null | (() => void)>(null);
const isMobileScreen = ref(isMobile());
/** フォーカス/編集後に文字数超過しているフィールド（true = 保存不可） */
const fieldLengthErrors = ref<Record<string, boolean>>({});

const onResize = debounce(() => {
  isMobileScreen.value = isMobile();
}, 150);

const pageEditStore = usePageEditStore();
const themeStore = useThemeStore();
const { fetchTheme, updateTheme } = themeStore;

// ////////////////////////////////////////
// Analytics
// ////////////////////////////////////////
const instance = getCurrentInstance();
const gtm = instance?.proxy.$gtm;
const analytics = gtm ? new ThemeAnalytics(gtm) : null;
const generateAnalytics = gtm ? new ThemeGenerateAnalytics(gtm) : null;

async function applyGeneratedTheme(event: Event) {
  const custom = event as CustomEvent<{ fields?: ThemeGenerateFields }>;
  const fields = custom.detail?.fields ?? {};
  const names = THEME_FIELD_NAMES.filter((name) => typeof fields[name] === "string" && fields[name] !== "");
  if (names.length === 0) {
    Vue.notify({
      title: t("themeGenerate.apply.empty"),
      type: "warn",
    });
    return;
  }

  const overwritten = names.some((name) => (theme.value[name] ?? "").trim().length > 0);
  if (overwritten) {
    const confirmed = await showOkCancelDialog({
      title: t("themeGenerate.apply.overwriteTitle"),
      content: t("themeGenerate.apply.overwriteMessage"),
      okButton: t("themeGenerate.apply.overwriteOk"),
      cancelButton: t("themeGenerate.apply.overwriteCancel"),
    });
    if (!confirmed) return;
  }

  const next = { ...theme.value };
  names.forEach((name) => {
    next[name] = fields[name] as string;
  });
  theme.value = next;

  scrollToThemeField(names[0]);

  Vue.notify({
    title: t("themeGenerate.apply.applied"),
    type: "success",
  });

  generateAnalytics?.trackApply({
    novelId: props.novelId,
    fieldCount: names.length,
    overwritten,
  });
}

onMounted(async () => {
  window.addEventListener("resize", onResize);
  window.addEventListener(THEME_GENERATE_APPLY_EVENT, applyGeneratedTheme);

  await fetchTheme(props.novelId);
  if (themeStore.theme.detail) {
    const detail: Record<string, string> = {};
    themeStore.theme.detail.forEach((item) => {
      detail[item.name] = item.value;
    });
    theme.value = detail;
    preTheme.value = cloneDeep(detail);
  }

  const pendingFields = consumePendingThemeGenerateApply(props.novelId);
  if (pendingFields) {
    await applyGeneratedTheme(
      new CustomEvent(THEME_GENERATE_APPLY_EVENT, { detail: { fields: pendingFields } })
    );
  }

  // 保存のショートカットキーの有効化と解除関数の取得
  unbindShortcutKeys.value = handleShortcutKeys([
    { trigger: TriggerKey.Meta, keys: ["s"], callback: () => submit("shortcut") },
    { trigger: TriggerKey.Ctrl, keys: ["s"], callback: () => submit("shortcut") },
  ]);
});

onBeforeUnmount(() => {
  // コンポーネントが破棄される前に保存のショートカットキーを解除
  if (unbindShortcutKeys.value) unbindShortcutKeys.value();
  window.removeEventListener("resize", onResize);
  window.removeEventListener(THEME_GENERATE_APPLY_EVENT, applyGeneratedTheme);
  // debounce 内に予約された setTimeout が破棄後に発火し、
  // 破棄済みコンポーネントの ref を更新するのを防ぐ
  onResize.cancel();
});

const isValidWordLength = computed(() => {
  // テーマの執筆予定文字数は未入力を許容する
  if (!theme.value["word-length"]) {
    return true;
  }

  // 半角数字のみで構成されているかチェックする正規表現
  const isNumeric = /^\d+$/.test(theme.value["word-length"]);
  // 半角数字のみである場合にtrueを返し、それ以外はfalseを返す
  return isNumeric;
});

const changed = computed(() => {
  const changed = !deepEqual(preTheme.value, theme.value);
  pageEditStore.setModified(changed);
  return changed;
});

const hasLengthError = computed(() => Object.values(fieldLengthErrors.value).some(Boolean));

const canSave = computed(() => changed.value && !hasLengthError.value);

function onFieldValidationChange(name: string, hasError: boolean) {
  if (fieldLengthErrors.value[name] === hasError) return;
  fieldLengthErrors.value = { ...fieldLengthErrors.value, [name]: hasError };
}

async function submit(trigger: ThemeSaveTrigger = "button") {
  if (!canSave.value) return;

  if (!isValidWordLength.value) {
    await showNotifyDialog({
      title: t("themePage.inputErrorTitle"),
      content: t("themePage.inputErrorMessage"),
    });
    return;
  }

  const detail = Object.keys(theme.value).map((key) => ({
    name: key,
    value: theme.value[key],
  }));

  const input = {
    novelId: props.novelId,
    detail: filterWordLengthDetails(detail),
  };

  // GA: 保存（updateTheme より先に飛ばし、ボタン押下〜送信のラグと
  // 直後の画面遷移によるリクエスト欠落を最小化する。失敗時の補正は別途）
  analytics?.trackSave({
    novelId: props.novelId,
    theme: theme.value,
    trigger,
  });

  await updateTheme(input);

  Vue.notify({
    title: t("themePage.saveSuccessMessage"),
    type: "success",
  });

  pageEditStore.setModified(false);
  preTheme.value = cloneDeep(theme.value);
}

function filterWordLengthDetails(details: Detail[]) {
  const index = details.findIndex((detail) => detail.name === "word-length");
  if (index === -1) return details; // word-length属性が存在しない場合、元の配列を返す

  const wordLengthDetail = details[index];
  if (wordLengthDetail.value === "" || wordLengthDetail.value === undefined) {
    // word-length属性が空文字またはundefinedの場合、その要素を除外
    return details.filter((_, idx) => idx !== index);
  }

  return details;
}

function formatWordLength() {
  const parsedValue = parseInt(theme.value["word-length"], 10);
  if (!Number.isNaN(parsedValue)) {
    theme.value["word-length"] = parsedValue.toString();
  }
}

async function openExample(contentKey: string) {
  await showExampleDialog({
    title: t("themePage.exampleDialogTitle"),
    content: t(`themePage.${contentKey}`),
  });
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.theme-editor {
  width: 100%;
  padding: 40px 0;

  @include mq("sp") {
    padding: 20px 0;
  }
}

.theme-section {
  /* 710px から約15%拡大（横幅に余裕があるときより広く使う） */
  max-width: 820px;
  margin: 0 auto 20px;
  padding: 0 40px;
  box-sizing: border-box;

  @include mq("sp") {
    padding: 0 16px;
  }
}

.sub-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-title {
  font-size: var(--font-size-md);
  margin: 0;
  font-weight: var(--font-weight-bold);
  margin-bottom: 16px;
}

.section-label {
  margin: 0 0 8px;
  font-size: var(--font-size-sm);
  color: var(--color-text-low);
  white-space: pre-line;
}

.input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;

  span {
    font-size: var(--font-size-sm);
    color: var(--color-text-normal);
    white-space: nowrap;
  }
}

.word-length {
  width: 256px;
  height: 40px;
  font-size: var(--font-size-sm);
  border: none;
  padding: 0 12px;
  border-radius: var(--radius-sm);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  box-sizing: border-box;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }

  &::placeholder {
    color: var(--color-text-low);
  }
}

.save-button-wrapper {
  max-width: 820px;
  margin: 32px auto 0;
  padding: 0 40px;
  box-sizing: border-box;
  display: flex;
  justify-content: flex-end;

  @include mq("sp") {
    padding: 0 16px;
  }
}

.save-button {
  padding: 12px 32px;
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  border: none;
  border-radius: var(--radius-rounded);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: opacity 0.2s;

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }

  &:hover:not(:disabled) {
    opacity: 0.85;
  }
}

.annotation {
  margin-top: 12px;
  font-size: var(--font-size-xs);
  color: var(--color-text-low);
}

.annotation-title {
  font-weight: var(--font-weight-bold);
  margin-bottom: 6px;
}

.annotation-grid {
  display: grid;
  grid-template-columns: auto auto;
  column-gap: 24px;
  row-gap: 2px;
  justify-content: start;
}

.view-example-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-xs);
  color: var(--color-primary);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    opacity: 0.7;
  }
}

.section-label--with-link {
  margin-bottom: 0;
}

.synopsis-edit-link {
  display: inline-block;
  margin-top: 4px;
  margin-bottom: 8px;
  font-size: var(--font-size-sm);
  color: var(--color-primary);
  text-decoration: none;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
}
</style>
